// composables/usePostsFeed.js
//
// Reusable posts-feed state machine, extracted verbatim from HomePage.vue so the
// seller home AND the upcoming supplier feed share one engine (and one copy of
// the strict pagination contract). The consumer owns its own UI — navbar,
// segmented control, filter sheet, sort actions, cart — and reads/drives the feed
// purely through the surface this returns.
//
// THE CONTRACT THIS PROTECTS (backend MultiPagination — see the BE→FE briefs
// `2026-06-26-home-feed-pagination.md` + `2026-06-26-feed-recency-windows.md`):
// the posts feed exposes two MUTUALLY-EXCLUSIVE pagination modes whose param key
// sets are DISJOINT. Mixing them is a hard HTTP 400, never a silent wrong order.
//
//   - 'cursor' (keyset on id): the default recency scroll AND any cursor-only feed
//     (e.g. bookmarks). Sends page[limit] (+ page[after] when loading more) and
//     NEVER a `sort` — the cursor column IS the order.
//   - 'page' (offset): every sorted view (-publishedAt, -bookmarks). Sends
//     page[number] + page[size] + sort, and NEVER cursor params.
//
// The mode-pick + param-build logic is extracted into the two pure exports below
// so it is unit-testable in isolation and lives in exactly one place.
import { useAxiosRepo } from '@pinia-orm/axios';
import { useRepo } from 'pinia-orm';
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';

const FILTER_DEBOUNCE_MS = 300;
// Must exceed FILTER_DEBOUNCE_MS so the debounced watcher's isRefreshing guard
// still blocks any second loadInitial triggered by a setFeed sort-clear mutation.
const REFRESH_FLAG_RESET_MS = 350;

/**
 * Pure: choose the pagination mode for a feed given the active sort.
 *
 * Cursor-only feeds (e.g. bookmarks) are ALWAYS cursor — sending a sort there is a
 * 400. Sort-capable feeds switch to offset 'page' mode the moment a sort is
 * applied; with no sort they stay on the keyset recency cursor.
 *
 * @param {{ cursorOnly?: boolean }} feedConfig
 * @param {string|null|undefined} sort
 * @returns {'cursor'|'page'}
 */
export function resolvePaginationMode(feedConfig = {}, sort = null) {
  if (feedConfig?.cursorOnly) return 'cursor';
  // A feed's OWN `sort` counts as a sort. The out-of-stock feed must always ride
  // `-waitingMembers`, so it is always page mode — never the id-keyed cursor,
  // which defines its own order and 400s the moment a sort rides with it.
  return feedConfig?.sort || sort ? 'page' : 'cursor';
}

/**
 * Pure: build the JSON:API query params for one feed request. The two key sets
 * are DISJOINT and must never coexist (mixing = HTTP 400):
 *   - cursor: page[limit] (+ page[after]); never `sort`.
 *   - page:   page[number] + page[size] + sort; never page[after]/page[limit].
 *
 * Filters serialize via axios's default `[]` bracket form, producing the shapes
 * the backend requires: filter[categories][id][]=<id> (a scalar 400s/500s) and
 * filter[channels][]=<id>.
 */
export function buildFeedParams({
  mode,
  pageSize,
  include = '',
  withCount = '',
  sort = null,
  cursor = null,
  pageNumber = 1,
  filters = {},
} = {}) {
  const params = {};
  if (include) params.include = include;
  if (withCount) params.withCount = withCount;

  if (mode === 'page') {
    // Offset mode — required for any sort. Never combine with cursor params.
    params['page[number]'] = pageNumber;
    params['page[size]'] = pageSize;
    params.sort = sort;
  } else {
    // Keyset cursor mode — recency + cursor-only feeds. No `sort` (it would 400).
    params['page[limit]'] = pageSize;
    if (cursor) params['page[after]'] = cursor;
  }

  if (filters.categories?.length) {
    params['filter[categories][id]'] = filters.categories;
  }
  if (filters.channels?.length) {
    params['filter[channels]'] = filters.channels;
  }
  if (filters.search) {
    params['filter[search]'] = filters.search;
  }
  // "Out of stock lately" — posts carrying at least one product with a LIVE
  // out-of-stock mark. `oos` is the one recognised value; anything else is
  // ignored rather than rejected, so this sends exactly that or nothing.
  //
  // ALWAYS PAIRED WITH `sort=-waitingMembers` (it was `-availabilitySince`), which the feed definition
  // carries. The sort WITHOUT this filter "works" but leads with posts that were
  // never out of stock — they sort on NULL, which PostgreSQL puts first on a
  // descending order — so the top of the list would be exactly the wrong rows.
  if (filters.availability) {
    params['filter[availability]'] = filters.availability;
  }
  // «تدبيسة» shelf — only posts with at least one piece still ON_SHELF (BE→FE
  // brief 2026-09-23-tadbeesa-shelf-filter). `1` is the value sent; any other
  // value is a no-op server-side, like `availability`. It exists because ANY
  // filter turns the feed's recency gate off, so a channel-filtered read on
  // its own returns every sold-out listing of the past year. Composes with
  // filter[channels] in both pagination modes.
  if (filters.resaleAvailable) {
    params['filter[resaleAvailable]'] = filters.resaleAvailable;
  }

  return params;
}

/**
 * The feed engine.
 *
 * @param {object}  options
 * @param {Record<string, FeedDefinition>} options.feeds   Map of feedKey → definition.
 * @param {string}  options.initialFeed                    Key of the feed shown first.
 * @param {string}  [options.include]                      JSON:API `include` paths.
 * @param {string}  [options.withCount]                    JSON:API `withCount`.
 * @param {number}  [options.pageSize=10]                  page[limit] / page[size].
 * @param {() => void} [options.onAfterRefresh]            Called after a successful reset
 *                                                         (e.g. scroll the list to top).
 *
 * FeedDefinition = {
 *   model:     PiniaOrmModel,                 // for save/flush + useAxiosRepo
 *   endpoint:  (filters) => string | null,    // resolver; null skips the request
 *   cursorOnly: boolean,                       // true → never page/sort mode
 *   sort?:     string,                         // a FIXED sort this feed always
 *                                              // rides, which forces page mode.
 *                                              // Wins over `filters.sort` (the
 *                                              // sort sheet), because a feed
 *                                              // defined BY its order has no
 *                                              // meaningful second ordering.
 *   staticFilters?: Record<string, string>,    // filter values merged into every
 *                                              // request on this feed — what
 *                                              // makes it a FEED and not a
 *                                              // filter the user can half-apply
 *   orderBy:   (repo, filters) => Item[],      // how to read sorted items from the ORM
 *   fetcher?:  ({ url, params, signal }) => Promise<{ data, meta }> | null,
 *                                              // OPTIONAL transport seam. When set,
 *                                              // it replaces the default axios call
 *                                              // and must return the SAME body shape
 *                                              // axios carries in `response.data`
 *                                              // ({ data, meta }). The engine still
 *                                              // owns mode-pick, param-build, cursor/
 *                                              // page advancement, and ORM save — only
 *                                              // the wire call is swapped. Used by the
 *                                              // supplier feed's mock/real service so
 *                                              // its endpoints are swappable in one
 *                                              // place. Omit it (HomePage) → default
 *                                              // useAxiosRepo path, unchanged.
 * }
 */
export function usePostsFeed(options) {
  const {
    feeds,
    initialFeed,
    include = '',
    withCount = '',
    pageSize = 10,
    onAfterRefresh = null,
  } = options;

  // ---- Feed selection ----
  const currentFeed = ref(initialFeed);
  const feedConfig = computed(() => feeds[currentFeed.value] ?? null);

  // ---- Filters (engine input; the consumer's filter UI reads/mutates this) ----
  const filters = ref({
    channels: [],
    categories: [],
    sort: null,
    search: '',
  });

  // ---- Pagination state (two mutually-exclusive modes) ----
  //  - cursor (keyset on id): recency scroll + cursor-only feeds → nextCursor.
  //  - page (offset): sorted views → currentPage / lastPage.
  const nextCursor = ref(null);
  const currentPage = ref(1);
  const lastPage = ref(1);
  const hasMore = ref(true);

  // ---- Loading / error ----
  const loading = ref(false); // first-load / refresh (covers the empty screen)
  const isLoadingMore = ref(false); // pagination (covers the list footer)
  const isRefreshing = ref(false); // pull-to-refresh / re-load guard
  const error = ref(null);

  // ---- Lifecycle / request control ----
  const isPageActive = ref(true);
  // shallowRef (NOT ref): holds the live AbortController, a non-reactive object
  // that is never rendered. A deep `ref` would expose `.value` as a reactive
  // PROXY of the controller, so the request-scoped `abortController.value ===
  // controller` identity check (supersession guard in fetchData) would compare a
  // proxy to the raw object and always be false. shallowRef stores the object
  // as-is, so identity holds.
  const abortController = shallowRef(null);
  let watcherTimeout = null;

  // ---- Derived ----
  const currentModel = computed(() => feedConfig.value?.model ?? null);
  const currentRepo = computed(() => (currentModel.value ? useRepo(currentModel.value) : null));
  const currentEndpoint = computed(() => feedConfig.value?.endpoint?.(filters.value) ?? null);
  const paginationMode = computed(() =>
    resolvePaginationMode(feedConfig.value, filters.value.sort),
  );

  // Items render from the ORM (not the raw response), ordered per-feed.
  const items = computed(() => {
    const cfg = feedConfig.value;
    const repo = currentRepo.value;
    if (!cfg || !repo) return [];
    return cfg.orderBy(repo, filters.value);
  });

  function buildParams({ cursor = null, pageNumber = 1 } = {}) {
    return buildFeedParams({
      mode: paginationMode.value,
      pageSize,
      include,
      withCount,
      // A feed's own sort WINS. The out-of-stock feed is defined by its order —
      // newest live mark first — so "sort this by popularity instead" is not a
      // thing it can mean, and letting the sort sheet override it would send a
      // list whose top rows are the least out-of-stock ones.
      sort: feedConfig.value?.sort || filters.value.sort,
      cursor,
      pageNumber,
      filters: { ...filters.value, ...(feedConfig.value?.staticFilters ?? {}) },
    });
  }

  async function fetchData(reset = false) {
    // Don't fetch while the page is parked (tab switched away / unmounting).
    if (!isPageActive.value) return;

    // Prevent concurrent requests.
    if (loading.value || (isLoadingMore.value && !reset)) return;

    // ---- Request-scoped supersession control ----
    // Supersede any in-flight request, then capture THIS request's controller in
    // a local const. Every later touch of the SHARED abortController/loading
    // state is gated on `isCurrent()` (this controller is still the active one).
    // Without this, a superseded request's unwind clobbers the successor: its
    // `finally` would null the live controller and flip the in-flight request's
    // loading flags off (breaking supersession + the top-of-fn concurrency guard
    // → double-save), and a fetcher that ignores the abort signal (e.g. the
    // supplier-feed mock) would resolve and save stale data over the winner.
    if (abortController.value) {
      abortController.value.abort();
    }
    const controller = new AbortController();
    abortController.value = controller;
    const isCurrent = () => abortController.value === controller;

    try {
      if (reset) {
        loading.value = true;
        isLoadingMore.value = false;
        error.value = null;
      } else {
        isLoadingMore.value = true;
      }

      const requestPage = reset ? 1 : currentPage.value + 1;
      const cursor = reset ? null : nextCursor.value;
      const params = buildParams({ cursor, pageNumber: requestPage });

      const model = currentModel.value;
      const url = currentEndpoint.value;
      const repo = currentRepo.value;
      // Optional transport seam (see FeedDefinition.fetcher). A feed may supply
      // its own fetcher returning the SAME `{ data, meta }` body an axios
      // response carries in `response.data` — the engine logic below is
      // identical either way. Feeds without a fetcher (HomePage) take the
      // default useAxiosRepo path, unchanged.
      const fetcher = feedConfig.value?.fetcher;

      // A null endpoint (e.g. bookmarks without an active member) skips the request.
      let response = null;
      if (url) {
        if (fetcher) {
          const body = await fetcher({
            url,
            params,
            signal: controller.signal,
          });
          response = body ? { response: { data: body } } : null;
        } else if (model) {
          response = await useAxiosRepo(model).api().get(url, {
            params,
            save: false,
            signal: controller.signal,
          });
        }
      }

      // Drop the result if a newer request superseded this one while it awaited.
      // The axios path rejects on abort (handled in catch), but a fetcher that
      // ignores the signal resolves regardless — without this guard it would
      // flush/save stale data and mutate the winner's pagination state.
      if (!isCurrent()) return;

      // Batch ORM operations across nextTick so the virtual list re-flows once.
      if (reset && repo) {
        await nextTick();
        repo.flush();
        await nextTick();
      }

      // Re-check after the flush's nextTick yields: a request superseded between
      // the first guard and here must NOT save stale rows or advance the winner's
      // pagination. Today the tail awaits are all microtask nextTicks, so no new
      // request (which originates from a macrotask — a timer or DOM event) can
      // interleave; this keeps the no-stale-mutation invariant explicit and safe
      // if a future refactor turns any tail await into real async I/O.
      if (!isCurrent()) return;

      if (response?.response?.data?.data && repo) {
        repo.save(response.response.data.data);
        await nextTick();
      }

      // meta.page differs by mode: cursor → { from, to, hasMore, perPage };
      // page → { currentPage, from, lastPage, perPage, to, total }.
      const pageData = response?.response?.data?.meta?.page || {};

      if (paginationMode.value === 'page') {
        // Offset mode — advance page[number] until currentPage reaches lastPage.
        currentPage.value = Number(pageData.currentPage ?? requestPage);
        lastPage.value = Number(pageData.lastPage ?? currentPage.value);
        hasMore.value = currentPage.value < lastPage.value;
      } else {
        // Cursor mode — `to` is the last post id, i.e. the next page[after] anchor.
        nextCursor.value = pageData.to ?? null;
        hasMore.value = pageData.hasMore === true;
      }

      await nextTick();
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      // Only the current request may surface an error — a superseded one must
      // not clobber the successor's error state.
      if (isCurrent()) {
        error.value = err.message || 'Failed to load posts';
      }
      console.error('[usePostsFeed] Fetch error:', err);
    } finally {
      // Only the still-current request resets the shared loading/controller
      // state; a superseded request leaves the successor's flags + controller
      // untouched.
      if (isCurrent()) {
        loading.value = false;
        isLoadingMore.value = false;
        abortController.value = null;
      }
    }
  }

  async function loadInitial() {
    // Prevent multiple simultaneous refreshes.
    if (isRefreshing.value) return;

    isRefreshing.value = true;

    try {
      error.value = null;
      nextCursor.value = null;
      currentPage.value = 1;
      lastPage.value = 1;
      hasMore.value = true;
      await fetchData(true);

      await nextTick();
      onAfterRefresh?.();
    } finally {
      // Reset the flag after all operations settle.
      setTimeout(() => {
        isRefreshing.value = false;
      }, REFRESH_FLAG_RESET_MS);
    }
  }

  // `refresh` IS the guarded reset loader — same operation as the first load.
  const refresh = loadInitial;

  async function loadMore() {
    if (!hasMore.value || loading.value || isLoadingMore.value || isRefreshing.value) return;
    await fetchData(false);
  }

  async function setFeed(feedKey) {
    if (currentFeed.value === feedKey) return;
    currentFeed.value = feedKey;
    // Switching feed drops any sort → the new feed starts on its recency cursor.
    filters.value.sort = null;
    await loadInitial();
  }

  function setFilters(partial) {
    filters.value = { ...filters.value, ...partial };
  }

  // ---- Debounced reload on filter change ----
  watch(
    () => [
      filters.value.categories,
      filters.value.channels,
      filters.value.sort,
      filters.value.search,
    ],
    () => {
      if (watcherTimeout) clearTimeout(watcherTimeout);
      watcherTimeout = setTimeout(async () => {
        if (!isRefreshing.value) {
          await loadInitial();
        }
      }, FILTER_DEBOUNCE_MS);
    },
    { deep: true },
  );

  // ---- Lifecycle ----
  // Park the feed: stop accepting fetches, abort any in-flight request, AND
  // clear its loading flags. The aborted request's `finally` is gated on
  // `isCurrent()` and we just nulled the controller, so it will NOT reset these
  // itself — and there is no in-flight owner left to. Leaving loading/isLoadingMore
  // stuck `true` would dead-lock the top-of-fetchData concurrency guard on
  // keep-alive re-activation (onActivated), silently freezing the feed until
  // remount. Also drops any pending filter-debounce reload.
  function parkAndAbort() {
    isPageActive.value = false;
    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
    loading.value = false;
    isLoadingMore.value = false;
    if (watcherTimeout) {
      clearTimeout(watcherTimeout);
      watcherTimeout = null;
    }
  }

  onDeactivated(parkAndAbort);
  onBeforeUnmount(parkAndAbort);

  onActivated(() => {
    isPageActive.value = true;
  });

  onMounted(async () => {
    await loadInitial();
  });

  return {
    items,
    currentFeed,
    filters,
    loading,
    isLoadingMore,
    isRefreshing,
    error,
    hasMore,
    loadInitial,
    loadMore,
    refresh,
    setFeed,
    setFilters,
  };
}
